import { AlertTriangle } from "lucide-react";
import { motion, type Variants } from "motion/react";
import { useState, type FormEvent } from "react";
import { flushSync } from "react-dom";
import { useNavigate } from "react-router-dom";

import { Logo } from "@/components/brand/Logo";
import { MapMotifBackground } from "@/components/login/MapMotifBackground";
import { ThemeScope } from "@/components/ThemeScope";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/store/auth-store";
import { cn } from "@/lib/utils";

const ERROR_COPY = "Incorrect email or password. Please try again.";
const MIN_LOADING_MS = 1100;

/**
 * Entrance choreography (premium-01 §8, Phase D). The card enters as a unit
 * (y: 20 → 0), then its rows stagger in — brand → heading → subtitle → email →
 * password → button — on a settling spring, ~65ms apart. Framer resolves each
 * row's delay from its `custom` index; the global <MotionConfig reducedMotion
 * ="user"> collapses the transforms so reduced-motion users get an instant,
 * opacity-only appearance.
 */
const cardVariants: Variants = {
  hidden: { opacity: 0, y: 20 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: [0, 0, 0.2, 1] },
  },
};

const rowVariants: Variants = {
  hidden: { opacity: 0, y: 8 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: 0.14 + i * 0.065,
      type: "spring",
      stiffness: 480,
      damping: 34,
    },
  }),
};

/**
 * Visitor Login (light · spacious) at full fidelity. Centered 404px card on the
 * map-motif backdrop, with default / loading / error states.
 *
 * Wired to the real auth client (`POST /core_auth/login`). On success → hand
 * off to the app via a View Transition (see {@link goToApp}). The prototype
 * trigger `password === "wrong"` forces the error state without a network
 * round-trip; any other failure (bad creds, offline) degrades gracefully to the
 * same banner.
 */
export function LoginPage() {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  /**
   * Login → app handoff. Progressive enhancement via the View Transitions API:
   * where `document.startViewTransition` exists (Chromium, Safari 18+) we run
   * the router navigation inside a transition (`flushSync` so React commits the
   * new route synchronously, giving the browser a clean old/new snapshot to
   * crossfade — see the `::view-transition-*` rules in ds.css). Firefox and
   * reduced-motion users fall through to a plain, instant navigate — identical
   * to the prior hard cut, never broken.
   */
  function goToApp() {
    const reduced =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;
    const start = (
      document as Document & {
        startViewTransition?: (cb: () => void) => unknown;
      }
    ).startViewTransition;

    if (reduced || typeof start !== "function") {
      navigate("/");
      return;
    }
    start.call(document, () => {
      flushSync(() => navigate("/"));
    });
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (loading) return;
    setError(false);
    setLoading(true);
    const startedAt = Date.now();

    const fail = async () => {
      await holdFor(startedAt);
      setError(true);
      setLoading(false);
    };

    // Prototype trigger: guaranteed error path, no network needed.
    if (password === "wrong") {
      await fail();
      return;
    }

    try {
      // Authenticate + persist the session token (powers the map's
      // transformRequest + the places/floors/geo calls).
      await signIn(email, password);
      await holdFor(startedAt);
      goToApp();
    } catch {
      // Bad credentials / offline → graceful error banner.
      await fail();
    }
  }

  return (
    <ThemeScope theme="light" density="spacious">
      <div className="relative grid min-h-screen place-items-center px-4">
        <MapMotifBackground />

        <motion.div
          variants={cardVariants}
          initial="hidden"
          animate="show"
          className="relative w-[404px] max-w-full border border-border bg-card shadow-login"
          style={{ borderRadius: 20, padding: "38px 36px 32px" }}
        >
          <motion.div variants={rowVariants} custom={0}>
            <Logo size={38} subtitle="Indoor Wayfinding" className="mb-7" />
          </motion.div>

          <motion.h1
            variants={rowVariants}
            custom={1}
            className="type-display text-fg"
          >
            Welcome back
          </motion.h1>
          <motion.p
            variants={rowVariants}
            custom={2}
            className="mt-1 text-sm text-muted-foreground"
          >
            Sign in to find your way around the venue.
          </motion.p>

          {error && (
            <div
              role="alert"
              className="mt-5 flex animate-shake items-start gap-2 rounded-md border border-danger-border bg-danger-bg px-3 py-2.5 text-sm text-danger-fg"
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              {ERROR_COPY}
            </div>
          )}

          <form className="mt-5 space-y-4" onSubmit={onSubmit} aria-label="Sign in">
            <motion.div variants={rowVariants} custom={3} className="space-y-1.5">
              <Label htmlFor="email" className="text-xs font-semibold text-fg-2">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="visitor@westfield.com"
                autoComplete="email"
                disabled={loading}
                className="h-[42px] rounded-md border-[1.5px] border-input px-[13px]"
              />
            </motion.div>

            <motion.div variants={rowVariants} custom={4} className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-xs font-semibold text-fg-2">
                  Password
                </Label>
                <button
                  type="button"
                  className="text-xs font-medium text-primary-600 hover:underline"
                >
                  Forgot?
                </button>
              </div>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
                disabled={loading}
                aria-invalid={error}
                className={cn(
                  "h-[42px] rounded-md border-[1.5px] px-[13px]",
                  error ? "border-[#F4A5A0]" : "border-input",
                )}
              />
            </motion.div>

            <motion.button
              variants={rowVariants}
              custom={5}
              type="submit"
              disabled={loading}
              className={cn(
                "btn-primary-press focus-ring flex h-11 w-full items-center justify-center gap-2 rounded-md text-md font-semibold text-primary-foreground shadow-btn",
                "focus-visible:outline-none",
                loading ? "bg-primary-600" : "bg-primary hover:bg-primary-600",
              )}
            >
              {loading ? (
                <>
                  <span className="login-spinner" aria-hidden />
                  Signing in…
                </>
              ) : (
                "Sign in"
              )}
            </motion.button>
          </form>

          <p className="mt-6 text-center text-xs text-muted-foreground">
            Trouble signing in?{" "}
            <button
              type="button"
              className="font-medium text-primary-600 hover:underline"
            >
              Contact venue staff
            </button>
          </p>
        </motion.div>
      </div>
    </ThemeScope>
  );
}

/** Keep the loading state visible for at least {@link MIN_LOADING_MS}. */
async function holdFor(startedAt: number): Promise<void> {
  const elapsed = Date.now() - startedAt;
  if (elapsed < MIN_LOADING_MS) {
    await new Promise((r) => setTimeout(r, MIN_LOADING_MS - elapsed));
  }
}
